/**
 * The ZTrading mark.
 *
 * Drawn rather than imported as an image: the topbar renders it at 22px and a
 * raster would be soft there, the candles in the full artwork turn to mush
 * below about 64px, and an SVG follows the palette instead of baking the
 * greens in. The full logo keeps the candle ladder; the compact one drops it,
 * because five candles inside a 22px glyph are noise rather than detail.
 */

export interface BrandProps {
  /** Height of the mark in pixels. The wordmark scales with it. */
  size?: number;
  /** Omit the ZTRADING wordmark and show the mark alone. */
  markOnly?: boolean;
  className?: string;
}

const GREEN = "#22c55e";

/** A rising ladder, in the 0-100 viewBox the mark is drawn in. */
const CANDLES = [
  { x: 30, y: 62, h: 12, wickTop: 56, wickBottom: 78 },
  { x: 40, y: 54, h: 14, wickTop: 48, wickBottom: 72 },
  { x: 50, y: 46, h: 14, wickTop: 40, wickBottom: 64 },
  { x: 60, y: 38, h: 14, wickTop: 32, wickBottom: 56 },
  { x: 70, y: 30, h: 14, wickTop: 24, wickBottom: 48 },
];

export function Brand({ size = 22, markOnly = false, className }: BrandProps) {
  const showCandles = size >= 64;

  return (
    <span
      className={className}
      style={{ display: "inline-flex", alignItems: "center", gap: size * 0.42 }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        role="img"
        aria-label="ZTrading"
        style={{ display: "block", flexShrink: 0 }}
      >
        {/* The Z: two slabs and the diagonal between them, sheared right so
            the letter leans into the direction the arrow travels. */}
        <path
          d="M22 14 H82 L46 58 H78 L70 74 H12 L48 30 H16 Z"
          fill="url(#zt-steel)"
        />
        {/* The arrow runs corner to corner behind the Z, emerging top-right.
            It is one stroke plus a head rather than a path with a barb, so it
            stays clean when the whole mark is 22 pixels tall. */}
        <path d="M14 86 L80 20" stroke={GREEN} strokeWidth="9" strokeLinecap="round" />
        <path d="M63 14 H88 V39 Z" fill={GREEN} />

        {showCandles && (
          <g fill={GREEN} stroke={GREEN} strokeWidth="2">
            {/* body y, body height, wick top, wick bottom — a rising ladder */}
            {CANDLES.map(({ x, y, h, wickTop, wickBottom }) => (
              <g key={x}>
                <line x1={x} y1={wickTop} x2={x} y2={wickBottom} />
                <rect x={x - 3.5} y={y} width="7" height={h} rx="1" />
              </g>
            ))}
          </g>
        )}

        <defs>
          <linearGradient id="zt-steel" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#b9c2d0" />
          </linearGradient>
        </defs>
      </svg>

      {!markOnly && (
        <span
          style={{
            fontWeight: 700,
            fontSize: size * 0.62,
            letterSpacing: size * 0.055,
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ color: GREEN }}>Z</span>
          <span style={{ color: "var(--text)" }}>TRADING</span>
        </span>
      )}
    </span>
  );
}
